import { Component, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { IGlobalTrackingForm } from '@campaign-form/campaign-form.interface';
import { CreatorsAndCtasService } from '@campaign-form/form-fields/creators-and-ctas/creators-and-ctas.service';
import { CtaDialogStore } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.store';
import { CtaTemplateCopyService } from '@campaign-form/form-fields/creators-and-ctas/cta-template-copy.service';
import { DeliverableListComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.component';
import { TDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { TranslateModule } from '@ngx-translate/core';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { debounceTime, Subscription } from 'rxjs';

@Component({
  imports: [DeliverableListComponent, TranslateModule],
  selector: 'app-keyword-tracking',
  styleUrl: './keyword-tracking.component.scss',
  templateUrl: './keyword-tracking.component.html',
})
export class KeywordTrackingComponent implements OnDestroy {
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly getValues = input.required<boolean>();

  public readonly canSave = output<boolean>();
  public readonly changed = output<boolean>();
  public readonly values = output<FormGroup<IGlobalTrackingForm>['value']>();

  public readonly showTrackings = signal<boolean>(true);

  public readonly form: FormGroup<IGlobalTrackingForm> = new FormGroup({
    campaignId: new FormControl<string | null>(null),
    id: new FormControl<string | null>(null),
    trackings: new FormArray<FormGroup<TDeliverableForm>>([]),
    version: new FormControl<number | null>(null),
  });

  private readonly ctaTemplateCopyService = inject(CtaTemplateCopyService);

  private readonly initialTrackingsIds = new Set<string>();

  private readonly subscriptions: Subscription[] = [];

  constructor() {
    this.createInitializeEffect();
    this.createFormSubscription();
    this.createGetValuesEffect();
  }

  public ngOnDestroy(): void {
    this.subscriptions?.forEach((subscription) => subscription?.unsubscribe());
  }

  private createInitializeEffect(): void {
    effect(() => {
      const campaign = this.campaign();

      if (!campaign) {
        return;
      }

      this.initialize();
    });
  }

  private initialize(): void {
    this.changed.emit(false);

    this.setInitialTrackingsIds();

    this.form.patchValue({
      campaignId: this.campaign()?.id,
      id: this.campaign()?.globalTracking?.id,
      version: this.campaign()?.globalTracking?.version,
    });
    this.assignTracking();
  }

  private assignTracking(): void {
    const trackings = this.campaign()?.globalTracking
      ? CreatorsAndCtasService.getApiDeliverables(this.campaign()?.globalTracking!)
      : [];

    this.showTrackings.set(false);

    requestAnimationFrame(() => {
      this.form.setControl(
        'trackings',
        new FormArray<FormGroup<TDeliverableForm>>(this.ctaTemplateCopyService.createDeliverableFormGroups(trackings)),
      );

      this.showTrackings.set(true);
    });
  }

  private createFormSubscription(): void {
    this.subscriptions.push(
      this.form.valueChanges.pipe(debounceTime(100)).subscribe(() => {
        this.changed.emit(this.form.invalid || this.form.dirty || this.hasSizeChanges());
        this.canSave.emit(this.form.valid);
      }),
    );
  }

  private createGetValuesEffect(): void {
    effect(() => {
      const getValues = this.getValues();

      if (!getValues) {
        return;
      }

      this.form.markAllAsTouched();

      if (this.form.valid) {
        this.values.emit(this.form.getRawValue());
      } else {
        const firstInvalidTracking = this.form.controls.trackings.controls.find((c) => c.invalid);
        const firstInvalidId = firstInvalidTracking?.get('id')?.value as string | null | undefined;

        if (firstInvalidId) {
          CtaDialogStore.setExpandedErrorDeliverablePanel(firstInvalidId);
        }
      }
    });
  }

  private hasSizeChanges(): boolean {
    const trackings = this.form.get('trackings')?.value;

    if (trackings?.length !== this.initialTrackingsIds.size) {
      return true;
    }

    for (const tracking of trackings ?? []) {
      if (tracking.id && !this.initialTrackingsIds.has(tracking.id)) {
        return true;
      }
    }

    return false;
  }

  private setInitialTrackingsIds(): void {
    this.campaign()?.globalTracking?.textTrackings?.forEach((tracking) => {
      this.initialTrackingsIds.add(tracking.id);
    });

    this.campaign()?.globalTracking?.audioTrackings?.forEach((tracking) => {
      this.initialTrackingsIds.add(tracking.id);
    });
  }
}
